<template>
  <div>
    <div v-if="latestUpload">
      <h3>
        <v-icon :color="latestUploadStatusDetails.color">{{
          latestUploadStatusDetails.icon
        }}</v-icon>
        {{ latestUploadStatusDetails.text }}
      </h3>
      <v-btn
        outlined
        rounded
        large
        color="primary"
        @click="navigateToUploadedWill(latestUpload.id)"
        >View uploaded will</v-btn
      >

      <ValidationObserver v-if="!isAdvisor" ref="observer">
        <v-form @submit.prevent="submit">
          <ValidationProvider
            id="uploaded_will_status"
            v-slot="{ errors }"
            name="uploaded_will_status"
            rules="required"
          >
            <v-radio-group
              v-model="status"
              :error-messages="errors"
              :disabled="!isAdmin && currentStatus == 'VALID'"
            >
              <v-radio
                label="Pending validation"
                value="PENDING"
                color="primary"
              />
              <v-radio label="Valid" value="VALID" color="primary" />
              <v-radio label="Invalid" value="INVALID" color="primary" />
              <v-radio
                label="Validity inconclusive"
                value="VALIDITY_INCONCLUSIVE"
                color="primary"
              />
            </v-radio-group>
          </ValidationProvider>

          <ValidationProvider
            id="comments"
            v-slot="{ errors }"
            name="comments"
            :rules="{ required: commentsRequired }"
          >
            <v-textarea
              v-model="comments"
              label="Comments"
              auto-grow
              outlined
              :error-messages="errors"
            />
          </ValidationProvider>
          <v-btn
            :loading="buttonLoading"
            rounded
            depressed
            large
            type="submit"
            color="primary"
          >
            Save
          </v-btn>
        </v-form>
      </ValidationObserver>
    </div>

    <p v-else>No will uploaded</p>

    <div v-if="latestUpload" class="latest-upload-container">
      <v-divider class="my-6" />
      <div class="status-container">
        <v-icon :color="latestUploadStatusDetails.color">{{
          latestUploadStatusDetails.icon
        }}</v-icon>
        <p class="status-text ml-2">
          Status:
          <span
            class="status-value"
            :style="{
              color: latestUploadStatusDetails.color,
            }"
            >{{ latestUploadStatusDetails.status }}</span
          >
        </p>
      </div>
      <div class="latest-upload-card">
        <div class="upload-details-card">
          <p class="mb-1">
            <strong>Version:</strong> {{ latestVersion.version }}
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Uploaded:</strong>
            {{ formattedUploadDate }}
          </p>

          <div v-if="latestUpload.comments" class="mb-3">
            <h5 class="mt-2">Comments:</h5>
            <p>{{ latestUpload.comments }}</p>
          </div>
          <div
            class="action-button-container"
            style="margin-bottom: 0 !important"
          >
            <GradientButton
              @gradientButtonClicked="navigateToUploadedWill(latestUpload.id)"
              :title="'View most recent uploaded will'"
              :callout="'right'"
              :fill="false"
            />
          </div>
        </div>
      </div>
      <v-divider class="my-6" />
    </div>

    <ErrorSnackbar v-model="showError" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </div>
</template>

<script>
  import { TESTATOR_QUERY } from '../../../../constants/graphql/testators';
  import ErrorSnackbar from '../../../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../../../mixins/form-helper-mixin';
  import {
    UPDATE_UPLOADED_WILL_MUTATION,
    CREATE_UPLOADED_WILL_PRESIGNED_URL_MUTATION,
  } from '../../../../constants/graphql/will-verifications';
  import SuccessSnackbar from '../../../common/SuccessSnackbar.vue';
  import {
    verificationStatus,
    latestUploadedWill,
    latestWillVersion,
  } from '../../../../constants/js/will-verification';

  export default {
    name: 'VerifyUploadedWill',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        status: this.formStatus(),
        comments: this.formComments(),
        showSuccess: false,
        successMessage: null,
      };
    },
    computed: {
      isAdvisor() {
        return this.$store.getters.isAdvisor;
      },
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      commentsRequired() {
        return (
          this.status == 'INVALID' || this.status == 'VALIDITY_INCONCLUSIVE'
        );
      },
      latestUpload() {
        return latestUploadedWill(this.testator.willVersions);
      },
      latestUploadStatusDetails() {
        return verificationStatus(this.latestUpload.status, this.icons);
      },
      currentStatus() {
        return latestUploadedWill(this.testator.willVersions).status;
      },
      latestVersion() {
        return latestWillVersion(this.testator?.willVersions);
      },
      formattedUploadDate() {
        if (!this.latestUpload?.createdAt) return '';
        const date = new Date(this.latestUpload.createdAt);
        return date.toISOString().split('T')[0];
      },
    },
    watch: {
      latestUpload(newValue) {
        if (newValue) {
          (this.status = this.formStatus()),
            (this.comments = this.formComments());
        }
      },
    },
    methods: {
      formStatus() {
        return latestUploadedWill(this.testator.willVersions)
          ? JSON.parse(
              JSON.stringify(
                latestUploadedWill(this.testator.willVersions).status
              )
            )
          : null;
      },
      formComments() {
        return latestUploadedWill(this.testator.willVersions)
          ? JSON.parse(
              JSON.stringify(
                latestUploadedWill(this.testator.willVersions).comments
              )
            )
          : null;
      },
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.updateUploadedWill();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      updateUploadedWill() {
        const uploadedWillInput = {
          status: this.status,
          comments: this.comments,
        };
        const id = this.latestUpload.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_UPLOADED_WILL_MUTATION,
            variables: {
              id,
              uploadedWillInput,
            },
            update: (cache, { data: { updateUploadedWill } }) => {
              this.updateCacheAfterUpdateUploadedWill(
                cache,
                updateUploadedWill
              );
            },
          })
          .then(data => {
            this.$emit('refreshTestator');
            this.buttonLoading = false;
            this.showSuccessMessage();
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateCacheAfterUpdateUploadedWill(cache, updateUploadedWill) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        var willVersion = data.testator.willVersions.find(
          willVersion => willVersion.id == updateUploadedWill.willVersionId
        );
        willVersion.status = updateUploadedWill.status;
        var uploadedWill = willVersion.uploadedWills.find(
          uploadedWill => uploadedWill.id == updateUploadedWill.id
        );
        uploadedWill = updateUploadedWill;

        cache.writeQuery({ ...query, data });
      },
      showSuccessMessage() {
        this.successMessage = 'Will verification successfully updated';
        this.showSuccess = true;
      },
      navigateToUploadedWill(id) {
        var newTab = window.open('', '_blank');
        this.$apollo
          .mutate({
            mutation: CREATE_UPLOADED_WILL_PRESIGNED_URL_MUTATION,
            variables: {
              id,
            },
          })
          .then(response => {
            newTab.location.href = response.data.createUploadedWillPresignedUrl;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .latest-upload-container {
    background-color: white;

    .status-container {
      display: flex;
      align-items: center;
      padding: 0 0 0 23px;
      height: 80px;
      box-sizing: border-box;

      .status-text {
        font-weight: 700;
        font-size: 16px;
        line-height: 150%;
        color: #282828;
        margin: 0;

        .status-value {
          font-weight: 600;
        }
      }
    }
  }

  .latest-upload-card {
    margin: 0;
    padding: 0;
    overflow: hidden;

    .upload-details-card {
      border-top: 2px solid #dcddde;
      border-left: 4px solid #429378;
      padding: 30px 30px 40px 30px;
      background: #f2f2f2;
    }
  }

  .action-button-container {
    margin-top: 30px;
    margin-bottom: 40px;
  }
</style>
